import { useState, useMemo, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppStore } from '@/store'
import type { PlanificacionSemanalRow } from '@/types'
import DateNavPicker from '@/components/common/DateNavPicker'
import { ViewModeSelect, type PlanVista } from '@/components/planning/ViewModeSelect'
import { fmtUds } from '@/utils/planningFormatters'
import { KpiCard } from '@/components/planning/KpiCard'
import { PAGE_KPIS } from '@/config/uiFlags'
import { PLAN_VISTA_CONFIG } from '@/config/planningVistas'
import { AlertsDrawer } from '@/components/planning/AlertsDrawer'
import { SlideOver } from '@/components/common/SlideOver'
import { FilterPanel } from '@/components/planning/FilterPanel'
import { FranjaRotura } from '@/components/planning/FranjaRotura'
import { GanttTrackHeader } from '@/components/planning/GanttTrackHeader'
import { RutaSection } from '@/components/planning/RutaSection'
import { MobileRutaSection, MobileLoadingSkeleton } from '@/components/planning/OrderCard'
import { usePlanningFilters } from '@/hooks/usePlanningFilters'
import { usePlanningData, type PlanningMode } from '@/hooks/usePlanningData'
import { useTimeTrack } from '@/hooks/useTimeTrack'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { BarraMarcas, useMarcasFilas } from '@/components/common/MarcasFilas'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { construirFilasGantt, exportarGanttExcel } from '@/utils/exportGanttExcel'

/** `PlanVista` es el vocabulario de la URL; `PlanningMode` el de las queries. */
const MODE_BY_VISTA: Record<PlanVista, PlanningMode> = {
  semanal: 'weekly',
  mensual: 'monthly',
  anual:   'yearly',
}

/**
 * Gantt de Planificación Avanzada — una sola página para los tres rangos.
 *
 * Todo lo que depende del rango temporal está en dos objetos: el `track`
 * (geometría del eje: columnas, posiciones, línea de hoy, etiqueta) y la `cfg`
 * (títulos, capacidad, claves de KPI y de Excel). El resto de la pantalla
 * —KPIs, filtros, agrupación por ruta, alertas, exportación— es idéntico en los
 * tres modos y por eso vive aquí una sola vez.
 */
export default function GanttPage({ vista }: { vista: PlanVista }) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  const { isMobile } = useBreakpoint()
  // Mismo ámbito que la pista de escritorio: `useMarcasFilas` devuelve el mismo
  // almacén, así que lo marcado en una anchura sigue marcado en la otra.
  const marcasGantt = useMarcasFilas('gantt')
  const {
    dark,
    weekOffset,  setWeekOffset,
    monthOffset, setMonthOffset,
    yearOffset,  setYearOffset,
  } = useAppStore()

  const cfg = PLAN_VISTA_CONFIG[vista]

  // Cada vista navega con su propio offset, guardado en el store para que
  // cambiar de rango y volver no pierda la posición.
  const offset    = vista === 'semanal' ? weekOffset    : vista === 'mensual' ? monthOffset    : yearOffset
  const setOffset = vista === 'semanal' ? setWeekOffset : vista === 'mensual' ? setMonthOffset : setYearOffset

  const track = useTimeTrack(vista, offset)

  const [selectedComp, setSelectedComp]           = useState<PlanificacionSemanalRow | null>(null)
  const [highlightedProdId, setHighlightedProdId] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const focusProd = searchParams.get('focus')

  function handleNavigateToProduction(prodid: string) {
    setHighlightedProdId(prodid)
    setTimeout(() => {
      document.getElementById(`gantt-row-${prodid}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 120)
    setTimeout(() => setHighlightedProdId(null), 2800)
  }

  const {
    producciones, planRows, isLoading, planByProd,
    totalUnidades, avanceGlobal, capacidadPct,
    prodsConRotura, coberturaPct, unidadesEnRiesgo, riesgoPct, proximasEntregas,
  } = usePlanningData(MODE_BY_VISTA[vista], offset, cfg.capacidadMax)

  // Llegada desde otra pantalla con ?focus=<OT>: resalta y hace scroll a esa fila
  // en cuanto los datos están cargados y la fila existe en el DOM.
  useEffect(() => {
    if (!focusProd || isLoading) return
    let raf = 0, tries = 0
    const tryFocus = () => {
      const el = document.getElementById(`gantt-row-${focusProd}`)
      if (el) {
        setHighlightedProdId(focusProd)
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        setTimeout(() => setHighlightedProdId(null), 2800)
        const next = new URLSearchParams(searchParams); next.delete('focus')
        setSearchParams(next, { replace: true })
        return
      }
      if (tries++ < 40) raf = requestAnimationFrame(tryFocus)
    }
    raf = requestAnimationFrame(tryFocus)
    return () => cancelAnimationFrame(raf)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusProd, isLoading])

  const {
    orden, articulo, componente, materialSelec, estados, rutasSelec, empresaSelec, tipoArticulo, rotura, roturaDelComponente, pendingStatus, retrasadas, almacenTab, opMin, opMax, sort, sortDir, allExpanded,
    setOrden, setArticulo, setComponente, setRotura, setRoturaDelComponente, setPendingStatus, setRetrasadas, setAlmacenTab,
    setOpMin, setOpMax, setSort, setSortDir, setAllExpanded, setTipoArticulo,
    toggleEstado, toggleMaterial, toggleRuta, toggleEmpresa, clearAll,
    allRutas, allAlmacenes, allEmpresas, isEmpresaAll, maxOpAll, filteredProds, gruposRuta, activeFilters, avp,
    opcionesArticulo, opcionesComponente,
    casaComponente, autoAbrirProds, busquedaNonce,
  } = usePlanningFilters(producciones, planRows, planByProd, prodsConRotura)

  // Todas las filas traen el mismo sello —lo pone la reconstrucción entera—, así
  // que basta el primero que llegue. Se enseña en la zona horaria del navegador
  // porque Fabric lo guarda en UTC y aquí eso son dos horas de menos.
  const fechaCalculo = useMemo(() => {
    const iso = planRows.find(r => r.fechaCalculo)?.fechaCalculo
    if (!iso) return null
    const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`)
    return isNaN(d.getTime()) ? null : d.toLocaleString()
  }, [planRows])

  const pageBg     = dark ? '-m-6 p-6 bg-slate-950 text-slate-100 min-h-full' : 'text-slate-900'
  const cardBg     = dark ? 'bg-slate-900 border-white/8' : 'bg-white border-slate-200'
  const mutedTxt   = dark ? 'text-slate-500' : 'text-slate-400'
  const mainTxt    = dark ? 'text-slate-50'  : 'text-slate-900'
  const btnBase    = 'text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors font-medium cursor-pointer'
  const btnNeutral = dark
    ? `${btnBase} border-white/10 bg-white/5 text-slate-300 hover:bg-white/10`
    : `${btnBase} border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm`

  function exportExcel() {
    exportarGanttExcel(
      construirFilasGantt({
        producciones: filteredProds,
        planRows,
        isEmpresaAll,
        fmtArt,
        etiquetaEstado: (s: number) => t(`planning.status.${s}`, { defaultValue: String(s) }),
        t,
      }),
      {
        nombreHoja: t(cfg.excelSheetKey),
        nombreHojaComponentes: t('planning.excel.componentsSheet'),
        fichero: t(cfg.excelFilenameKey),
        sufijo: track.exportStamp,
      },
    )
  }

  // La vista semanal rotula el rango en minúsculas dentro de la frase de "sin
  // producciones"; las otras usan la etiqueta tal cual (lleva nombre propio de
  // mes o año).
  const emptyLabel = vista === 'semanal' ? track.label.toLowerCase() : track.label

  const emptyState = (
    <div className={`py-16 text-center text-[12px] ${mutedTxt}`}>
      {activeFilters > 0
        ? t('planning.noResults')
        : t('planning.noProductionsFor', { label: emptyLabel })}
    </div>
  )

  return (
    <div className={`flex flex-col gap-4 ${isMobile ? '' : 'h-full min-h-0'} ${pageBg}`}>

      {/* ── Top bar ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className={`text-[15px] font-black ${mainTxt}`}>{t(cfg.titleKey)}</div>
          <div className={`text-[11px] mt-0.5 ${mutedTxt}`}>
            {t(cfg.subtitleKey)} · {t('planning.prodCount', { filtered: filteredProds.length, total: producciones.length })}
            {activeFilters > 0 && (
              <span className="ml-1 text-sky-500 font-semibold">
                {t('planning.activeFilters', { count: activeFilters })}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Aviso de entregas próximas */}
          {!isLoading && proximasEntregas > 0 && (
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[11px] font-semibold ${
              dark ? 'bg-amber-950/40 border-amber-500/30 text-amber-400'
                   : 'bg-amber-50 border-amber-200 text-amber-700'
            }`}>
              <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="8" cy="8" r="6" />
                <path d="M8 5v3.5l2 2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {t('planning.deliveryChip', { count: proximasEntregas })}
            </div>
          )}
          {/* Exportar a Excel */}
          <button
            onClick={exportExcel}
            disabled={filteredProds.length === 0 || isLoading}
            className={`${btnNeutral} disabled:opacity-40 disabled:cursor-not-allowed`}
            title={t('common.exportExcel')}
          >
            <svg className="w-3.5 h-3.5 inline mr-1" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12h10M8 3v7M5 7l3 3 3-3"/>
            </svg>
            {t('common.exportExcel')}
          </button>
          {/* Selector de rango: semanal / mensual / anual */}
          <ViewModeSelect dark={dark} />
          {/* Navegación del rango activo */}
          <DateNavPicker
            mode={cfg.navMode}
            value={offset}
            onChange={setOffset}
            label={track.label}
            dark={dark}
            btnClass={btnNeutral}
          />
        </div>
      </div>

      {/* ── KPIs ─────────────────────────────────────────────────────────── */}
      {PAGE_KPIS.advanced && (
      <div className="flex gap-3 flex-wrap">
        <KpiCard dark={dark}
          label={t(cfg.demandKpiKey)}
          value={isLoading ? '—' : fmtUds(totalUnidades)}
          sub={t('planning.kpi.committedDemandSub', { count: producciones.length, pct: avanceGlobal })}
          progress={isLoading ? undefined : capacidadPct}
          progressColor="bg-sky-400"
        />
        <KpiCard dark={dark}
          label={t(cfg.capacityKpiKey)}
          value={fmtUds(cfg.capacidadMax)}
          sub={t('planning.kpi.availableCapacitySub', { pct: capacidadPct, routes: gruposRuta.length })}
          progress={isLoading ? undefined : capacidadPct}
          progressColor={capacidadPct >= 90 ? 'bg-red-400' : capacidadPct >= 70 ? 'bg-yellow-400' : 'bg-emerald-400'}
        />
        <KpiCard dark={dark}
          label={t('planning.kpi.materialCoverage')}
          value={isLoading ? '—' : `${coberturaPct}%`}
          sub={prodsConRotura.size === 0
            ? t('planning.kpi.materialCoverageNoBreaks')
            : t('planning.kpi.materialCoverageBreaks', { count: prodsConRotura.size })}
          progress={isLoading ? undefined : coberturaPct}
          progressColor={coberturaPct >= 80 ? 'bg-emerald-400' : coberturaPct >= 50 ? 'bg-yellow-400' : 'bg-red-400'}
        />
        <KpiCard dark={dark}
          label={t('planning.kpi.unitsAtRisk')}
          value={isLoading ? '—' : fmtUds(unidadesEnRiesgo)}
          sub={unidadesEnRiesgo === 0
            ? t('planning.kpi.unitsAtRiskNone')
            : t('planning.kpi.unitsAtRiskSub', { pct: riesgoPct, count: prodsConRotura.size })}
          progress={isLoading ? undefined : riesgoPct}
          progressColor={unidadesEnRiesgo === 0 ? 'bg-emerald-400' : riesgoPct > 30 ? 'bg-red-400' : 'bg-amber-400'}
          accent={unidadesEnRiesgo > 0 ? 'red' : undefined}
        />
      </div>
      )}

      {/* Cuándo se calculó el dato, no cuándo se leyó.
          El semáforo de material NO se calcula al abrir la pantalla: sale de una
          tabla que reconstruye un notebook A MANO. Un «viene de camino» es una
          afirmación sobre el futuro, y sin saber de cuándo es el dato no se
          puede juzgar: una entrega que se retrasó ayer sigue pintando naranja
          hoy. Por eso el sello va aquí arriba y no escondido en un tooltip. */}
      {!isLoading && (
        <p className={`text-[11px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
          {fechaCalculo
            ? t('planning.consumo.calculadoEl', { fecha: fechaCalculo })
            : t('planning.consumo.calculadoNunca')}
        </p>
      )}

      {/* ── Panel de filtros ─────────────────────────────────────────────── */}
      <FilterPanel
        dark={dark}
        producciones={producciones}
        orden={orden} articulo={articulo} componente={componente}
        opcionesArticulo={opcionesArticulo} opcionesComponente={opcionesComponente}
        materialSelec={materialSelec} estados={estados} rutasSelec={rutasSelec} empresaSelec={empresaSelec} tipoArticulo={tipoArticulo} rotura={rotura}
        roturaDelComponente={roturaDelComponente} setRoturaDelComponente={setRoturaDelComponente}
        pendingStatus={pendingStatus}
        retrasadas={retrasadas} almacenTab={almacenTab}
        opMin={opMin} opMax={opMax} sort={sort} sortDir={sortDir} activeFilters={activeFilters}
        allRutas={allRutas} allAlmacenes={allAlmacenes} allEmpresas={allEmpresas} isEmpresaAll={isEmpresaAll} maxOpAll={maxOpAll} avp={avp}
        setOrden={setOrden} setArticulo={setArticulo} setComponente={setComponente} setRotura={setRotura} setPendingStatus={setPendingStatus} setRetrasadas={setRetrasadas}
        setAlmacenTab={setAlmacenTab} setOpMin={setOpMin} setOpMax={setOpMax}
        setSort={setSort} setSortDir={setSortDir} setTipoArticulo={setTipoArticulo}
        toggleMaterial={toggleMaterial} toggleEstado={toggleEstado} toggleRuta={toggleRuta} toggleEmpresa={toggleEmpresa} clearAll={clearAll}
      />

      {/* ── Timeline (desktop) / Tarjetas (móvil) ────────────────────────── */}
      {isMobile ? (
        <div className="flex flex-col gap-2">
          {/* En escritorio la franja vive en la cabecera de la pista; aquí no hay
              pista, y ponerla dentro de cada grupo por ruta repetiría el mismo
              total una vez por ruta. Sin ella, en móvil se podía marcar pero no
              quitar todas de golpe. */}
          <BarraMarcas dark={dark} marcas={marcasGantt} />
          {isLoading && <MobileLoadingSkeleton dark={dark} />}
          {!isLoading && gruposRuta.length === 0 && emptyState}
          {!isLoading && gruposRuta.map(g => (
            <MobileRutaSection
              key={g.ruta} ruta={g.ruta}
              producciones={g.producciones} planRows={g.planRows}
              dark={dark}
              onSelectComponent={setSelectedComp}
              highlightedProdId={highlightedProdId}
              globalExpanded={allExpanded}
              autoAbrirProds={autoAbrirProds}
              resalta={casaComponente}
              busquedaNonce={busquedaNonce}
            />
          ))}
        </div>
      ) : (
        <div
          className={`rounded-xl border overflow-hidden shadow-sm flex flex-col flex-1 min-h-0 ${cardBg}`}
          style={{ minHeight: 280 }}
        >
          <GanttTrackHeader track={track} dark={dark} />

          {/* Cuándo rompe el componente buscado. Sólo aparece si hay algo que
              buscar: sin componente no hay serie que pintar. */}
          {casaComponente && !isLoading && (
            <FranjaRotura
              planRows={planRows}
              casaComponente={casaComponente}
              track={track}
              dark={dark}
            />
          )}

          {/* Filas — scroll independiente */}
          <div className="flex-1 overflow-y-auto scroll-thin">
            {isLoading && (
              <div className={`py-16 text-center text-[12px] ${mutedTxt}`}>{t('planning.loading')}</div>
            )}

            {!isLoading && gruposRuta.length === 0 && emptyState}

            {!isLoading && gruposRuta.length > 0 && (
              <div className="p-2">
                {gruposRuta.map(g => (
                  <RutaSection
                    key={g.ruta} ruta={g.ruta}
                    producciones={g.producciones} planRows={g.planRows}
                    track={track}
                    dark={dark}
                    onSelectComponent={setSelectedComp}
                    highlightedProdId={highlightedProdId}
                    globalExpanded={allExpanded}
                    isEmpresaAll={isEmpresaAll}
                    autoAbrirProds={autoAbrirProds}
                    resalta={casaComponente}
                    busquedaNonce={busquedaNonce}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <AlertsDrawer
        producciones={producciones}
        planRows={planRows}
        prodsConRotura={prodsConRotura}
        dark={dark}
        onNavigate={handleNavigateToProduction}
      />

      {selectedComp && (
        <SlideOver comp={selectedComp} dark={dark} onClose={() => setSelectedComp(null)} />
      )}

      {/* ── Botón flotante agrupar/desagrupar ── */}
      <button
        onClick={() => setAllExpanded(!allExpanded)}
        className={`fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-2 rounded-full shadow-lg text-[12px] font-bold transition-all ${
          dark
            ? 'bg-slate-700 text-slate-100 hover:bg-slate-600 border border-white/10'
            : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200 shadow-md'
        }`}
      >
        <span>{allExpanded ? '▾' : '▸'}</span>
        <span>{allExpanded ? t('planning.groupAll') : t('planning.ungroupAll')}</span>
      </button>
    </div>
  )
}
